import { useListOrganizations } from '@/queries/organizations.queries';
import { useCreateResourceMutation, useRemoveResourceMutation } from '@/queries/resources.queries';
import IBookingRules from '@/types/IBookingRules';
import IResource from '@/types/IResource';
import { useToast } from '@chakra-ui/react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router';

interface IResourceDetailsSuccessPageArgs {
	resource: IResource;
}

export function useAvailabilityDetailsController(args: IResourceDetailsSuccessPageArgs) {
	const { t } = useTranslation();
	const history = useHistory();
	const createResourceMutation = useCreateResourceMutation();
	const removeResourceMutation = useRemoveResourceMutation();
	const organizationsQuery = useListOrganizations();
	const toast = useToast();

	const { register, handleSubmit, control, formState } = useForm({
		defaultValues: args.resource,
		mode: 'all',
	});

	const onSubmit = handleSubmit((data) => {
		handleSave(data);
	});

	function handleRemove() {
		removeResourceMutation
			.mutateAsync({ id: args.resource.id })
			.then(() => {
				toast({
					isClosable: true,
					title: t('Resource deleted'),
					status: 'success',
				});
				history.goBack();
			})
			.catch(() => {
				toast({
					isClosable: true,
					title: t('Error deleting resource'),
					status: 'error',
				});
			});
	}

	function handleSave(updates: Partial<IResource>) {
		return createResourceMutation
			.mutateAsync({
				...args.resource,
				...updates,
			})
			.then(() => {
				toast({
					isClosable: true,
					title: t('Resource updated successfully'),
					status: 'success',
				});
			})
			.catch(() => {
				toast({
					isClosable: true,
					title: t('Failed to update resource'),
					status: 'error',
				});
			});
	}

	function handleBlur(e: React.ChangeEvent<HTMLInputElement>) {
		register(e.target.id as any).onBlur(e);
		onSubmit();
	}

	function handleBookingRulesChanged(update: Partial<IBookingRules>) {
		handleSave({ bookingRules: { ...args.resource.bookingRules, ...update } });
	}

	return {
		goBack: history.goBack,
		handleRemove,
		register,
		handleBlur,
		handleBookingRulesChanged,
		handleSave,
		t,
		control,
		formState,
		organizationsQuery,
	};
}
